import React from 'react';
import { Info, MapPin, RefreshCw, AlertCircle } from 'lucide-react';
import { getConsularDistrict } from './visaLogic';
import { provinceData } from '../shared/ProvinceCitySelect';

/**
 * 签证助手 - 左侧输入面板
 * 收集用户信息，包括：
 * 1. 基本信息（目的、年龄）
 * 2. 地点信息（省份、实时领区）
 * 3. 续签信息（条件显示）
 * 4. EVUS信息
 */
const AssistantInputPanel = ({ userInputs, setUserInputs, computedResults }) => {
  const handleInputChange = (field, value) => {
    setUserInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 处理"是否首次申请"变化时的逻辑
  const handleIsFirstTimeChange = (value) => {
    handleInputChange('isFirstTime', value);
    // 如果改为"是"，清空续签相关字段
    if (value === true) {
      setUserInputs(prev => ({
        ...prev,
        isFirstTime: true,
        lastVisaType: '',
        lastVisaFullValidity: null,
        lastVisaExpiredMonths: '',
        ageWhenLastIssued: '',
        applyInResidenceCountry: null,
        everRefused: false
      }));
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-gray-100 p-6 shadow-md sticky top-4 max-h-[calc(100vh-100px)] overflow-y-auto">
      {/* 基本信息 */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <Info className="w-5 h-5 mr-2 text-blue-600" />
          基本信息
        </h3>

        {/* 申请目的 */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            申请目的
          </label>
          <div className="flex gap-3 flex-wrap">
            {['旅游', '探亲', '商务'].map(purpose => (
              <button
                key={purpose}
                onClick={() => handleInputChange('purpose', purpose)}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  userInputs.purpose === purpose
                    ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                }`}
              >
                {purpose}
              </button>
            ))}
          </div>
        </div>

        {/* 年龄 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            您的年龄
            {userInputs.age && parseInt(userInputs.age, 10) < 18 && (
              <span className="text-xs text-amber-600 ml-2">（未成年人需特殊材料）</span>
            )}
          </label>
          <input
            type="number"
            value={userInputs.age}
            onChange={(e) => handleInputChange('age', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="请输入年龄"
            min="0"
            max="120"
          />
        </div>
      </div>

      {/* 地点信息 */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-green-600" />
          地点信息
        </h3>

        {/* 省份选择 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            常住地省份
          </label>
          <select
            value={userInputs.province}
            onChange={(e) => handleInputChange('province', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
          >
            <option value="">-- 请选择省份 --</option>
            {Object.entries(provinceData).map(([key, province]) => (
              <option key={key} value={key}>
                {province.name}
              </option>
            ))}
          </select>
        </div>

        {/* 实时显示领区 */}
        {computedResults.consularDistrict && userInputs.province && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 font-medium">
              📍 您的领区：<strong>{computedResults.consularDistrict}领事馆</strong>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              请在该领事馆体系范围内进行申请
            </p>
          </div>
        )}
      </div>

      {/* 续签信息 */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <RefreshCw className="w-5 h-5 mr-2 text-purple-600" />
          签证申请情况
        </h3>

      {/* 签证申请状态 - 关键选择 */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          您的签证申请状态
        </label>
        <div className="space-y-2">
          {[
            { label: '需要申请签证（首次或续签）', value: 'need_apply', desc: '需要填写DS-160表格、参加面谈' },
            { label: '已有有效签证（只需EVUS）', value: 'have_visa', desc: '签证仍在有效期内，只需完成EVUS登记' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => handleInputChange('visaStatus', option.value)}
              className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                userInputs.visaStatus === option.value
                  ? 'bg-blue-50 border-blue-500 shadow-sm'
                  : 'bg-white border-gray-300 hover:border-blue-300'
              }`}
            >
              <div className="font-medium text-gray-900">{option.label}</div>
              <div className="text-xs text-gray-600 mt-1">{option.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* 仅当选择"需要申请签证"时显示"是否首次申请" */}
      {userInputs.visaStatus === 'need_apply' && (
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            是否首次申请美国签证？
          </label>
          <div className="flex gap-3">
            {[
              { label: '是', value: true },
              { label: '否', value: false }
            ].map(option => (
              <button
                key={`${option.value}`}
                onClick={() => handleIsFirstTimeChange(option.value)}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                  userInputs.isFirstTime === option.value
                    ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 仅当选择"已有有效签证"时显示提示 */}
      {userInputs.visaStatus === 'have_visa' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-5">
          <p className="text-sm text-green-700 font-medium">
            ✅ 您已有有效签证，无需填写DS-160或参加面谈，只需完成EVUS登记即可。
          </p>
        </div>
      )}

        {/* 续签详细信息 - 仅当选择"需要申请"且"否（续签）"时显示 */}
        {userInputs.visaStatus === 'need_apply' && !userInputs.isFirstTime && (
          <div className="pl-4 border-l-4 border-blue-300 space-y-4 mt-6 pt-4">
            {/* 上次签证类型 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                上次签证类型
              </label>
              <select
                value={userInputs.lastVisaType}
                onChange={(e) => handleInputChange('lastVisaType', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- 请选择 --</option>
                <option value="B1/B2">B1/B2（商务/旅游）</option>
                <option value="B1">B1（商务）</option>
                <option value="B2">B2（旅游）</option>
                <option value="F1">F1（学生）</option>
                <option value="H1B">H1B（工作）</option>
                <option value="其他">其他</option>
              </select>
            </div>

            {/* 上次是否全有效期 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                上次签证是否为全有效期？
                <span className="text-xs text-blue-600 ml-1">（未被缩短）</span>
              </label>
              <div className="flex gap-3">
                {[
                  { label: '是', value: true },
                  { label: '否', value: false },
                  { label: '不确定', value: null }
                ].map(option => (
                  <button
                    key={`${option.value}`}
                    onClick={() => handleInputChange('lastVisaFullValidity', option.value)}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                      userInputs.lastVisaFullValidity === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 上次到期月数 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                上次签证距今已过期多少个月？
                <span className="text-xs text-blue-600 ml-1">（0-12个月内）</span>
              </label>
              <input
                type="number"
                value={userInputs.lastVisaExpiredMonths}
                onChange={(e) => handleInputChange('lastVisaExpiredMonths', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="例如：6"
                min="0"
                max="12"
              />
            </div>

            {/* ⚠️ 关键字段：上次签发时年龄 */}
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                ⚠️ 上次签证签发时您的年龄（关键字段）
              </label>
              <input
                type="number"
                value={userInputs.ageWhenLastIssued}
                onChange={(e) => handleInputChange('ageWhenLastIssued', e.target.value)}
                className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
                placeholder="例如：22"
                min="0"
                max="120"
              />
              <p className="text-xs text-red-600 mt-2">
                💡 免面谈必须≥18岁。这是2024年后新政策，不再按当前年龄自动免面谈。
              </p>
            </div>

            {/* 是否在居住国申请 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                是否在居住国/国籍国申请？
              </label>
              <div className="flex gap-3">
                {[
                  { label: '是', value: true },
                  { label: '否（第三国）', value: false },
                  { label: '不确定', value: null }
                ].map(option => (
                  <button
                    key={`${option.value}`}
                    onClick={() => handleInputChange('applyInResidenceCountry', option.value)}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                      userInputs.applyInResidenceCountry === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 是否被拒签过 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                是否曾被美国签证拒签过？
              </label>
              <div className="flex gap-3">
                {[
                  { label: '是', value: true },
                  { label: '否', value: false }
                ].map(option => (
                  <button
                    key={`${option.value}`}
                    onClick={() => handleInputChange('everRefused', option.value)}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                      userInputs.everRefused === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* EVUS信息 - 仅当选择"需要申请"时显示 */}
      {userInputs.visaStatus === 'need_apply' && (
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
          EVUS信息
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            您是否持有10年有效期的B1/B2签证？
          </label>
          <div className="flex gap-3">
            {[
              { label: '是', value: true },
              { label: '否', value: false }
            ].map(option => (
              <button
                key={`${option.value}`}
                onClick={() => handleInputChange('has10YearVisa', option.value)}
                className={`flex-1 px-4 py-2 rounded-lg border-2 transition-all ${
                  userInputs.has10YearVisa === option.value
                    ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            中国护照持有者 + 10年B类签证 = 出发前必须完成EVUS登记
          </p>
        </div>
      </div>
      )}

      {/* 提示信息 */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
        <p>
          💡 <strong>提示：</strong> 以上信息仅用于实时生成您的个性化材料清单和流程，不作为最终法律依据。
        </p>
      </div>
    </div>
  );
};

export default AssistantInputPanel;
